import { isVeloSite } from '../..';
import {
  getMdHeadingId,
  getPathnameSuffix,
  isMailUrl,
  pathToLink,
  removeRouterBasename,
  removeStartingProtocol,
  removeStartingSlash,
} from '../index';
import {
  getArticleNameFromPathname,
  getCategoryNameFromPathname,
} from '../../../client/LoggerFactory/BiAnalyticsLogger/events/utils';
import { sectionPrefix } from '../../../client/components/NavigationMenu/utils';
import { TYPES } from '../../flattenTree/utils/consts';
import { NEW_GUIDE_NAME } from '../../treeVisitors/createGuideFromIndexPageDescriptionVisitor';
import { toUrlFormat } from '../../treeVisitors';
import {
  isRelativeLinkToMdFile,
  getRelativeMdLink,
} from '../relativeMarkdownLinks';
import { replaceMdLinksSpaces } from '../../../Store/Utils/transformRelativeMdLinks';
import { NODE_TYPES } from '../../typeUtils';
import {
  getNodeByLink,
  isLinkExistInDocs,
} from '../../../Store/Utils/treeUtils';

export const isRelativeUrl = (url = '') =>
  !(
    url.startsWith('www.') ||
    url.startsWith('http:') ||
    url.startsWith('https:')
  );

const corvidApiBaseUrl = 'www.wix.com/corvid/reference';
const veloApiBaseUrl = 'www.wix.com/velo/reference';
const isAbsoluteOldCorvidUrl = (url) => url && url.includes(corvidApiBaseUrl);

const getHashPart = (
  hashParts,
  prefix,
  shouldAddPrefix,
  mdUrlFormat,
  pathnameWithoutRouterBasename,
  store,
) => {
  const _isLinkToCurrentPrefix = isLinkToCurrentPrefix(
    pathnameWithoutRouterBasename,
    prefix,
    store,
    mdUrlFormat,
  );
  const prefixPartPathname = shouldAddPrefix && prefix ? `${prefix}/` : '';

  const mdNamePartPathname =
    shouldAddPrefix && _isLinkToCurrentPrefix && mdUrlFormat
      ? `${mdUrlFormat}/`
      : '';

  return hashParts.length
    ? `/${prefixPartPathname}${mdNamePartPathname}${hashParts.join('/')}`
    : '';
};

const removeCorvidOldApiBaseUrl = (url) =>
  url.replace(corvidApiBaseUrl, '').replace(veloApiBaseUrl, ''); // TODO remove replace of corvidOldApiBaseUrl once the migration to velo is over

const removeLastSectionOfPathname = (pathname) => {
  const pathnameSections = pathname.split('/');
  pathnameSections.pop();
  return pathnameSections.join('/');
};

const getMemberId = (relativeUrl, store) => {
  const relativeUrlLowerCased = relativeUrl.toLowerCase();
  const categoryName = getCategoryNameFromPathname(
    relativeUrlLowerCased,
    store,
  );
  const articleName = getArticleNameFromPathname(relativeUrlLowerCased, store);
  return articleName ? `${categoryName}/${articleName}` : categoryName;
};

const _shouldLinkToGeneratedDescriptionGuide = (relativeUrl, store) => {
  const memberId = getMemberId(removeLastSectionOfPathname(relativeUrl), store);
  const treeEntry = getNodeByLink(store.getFullTree(), `/${memberId}`);
  return (
    treeEntry &&
    (treeEntry.data?.propType === TYPES.SECTION ||
      treeEntry.data?.propType === TYPES.CATEGORY)
  );
};

const isLinkToCurrentPrefix = (
  wixDocsUrlToArticle,
  prefix,
  store,
  shouldLinkToGeneratedDescriptionGuide,
) => {
  /*
  We will now see whether the wixDocs format link starts with `/${prefix}`. This is done in order to see
  if the link is linking to somewhere which is nested in relation to where the link is at.

  Why the difference needs to be smaller than 3?
  Say the link is at /$w. Sp, prefix of the article where the link is at is "$w".
  The link's value (aka wixDocsUrlToArticle) can be something like this - /$w/$w (link is not in different prefix), difference is 1.
  The link's value can be "/$w/$w/la-la" as well. Link is to a mdheading for sure by this point -
  so the different is 2, but the prefix of the article we link to is the same - "$w".

  Say we are in a guide which is inside of a tag. For instance, in `category/tag` prefix. The guide will be visible in `/category` already.
   */
  const prefixLink = pathToLink(prefix);
  if (shouldLinkToGeneratedDescriptionGuide) {
    const prefixOfDestination = removeStartingSlash(
      removeLastSectionOfPathname(wixDocsUrlToArticle),
    );
    if (prefixOfDestination) {
      return prefix === prefixOfDestination;
    }
  }
  const isDistanceBetweenDepthsAllowed = (linkFrom, maxDistanceAllowed) => {
    const distance =
      wixDocsUrlToArticle.split('/').length - linkFrom.split('/').length;
    return distance < maxDistanceAllowed;
  };
  const isDestinationNestedInLinkFrom = (linkFrom, maxDistanceAllowed) =>
    wixDocsUrlToArticle.startsWith(linkFrom) &&
    isDistanceBetweenDepthsAllowed(linkFrom, maxDistanceAllowed);

  if (isDestinationNestedInLinkFrom(prefixLink, 3)) {
    return true;
  }

  const node = getNodeByLink(store.getFullTree(), prefixLink);
  const isLinkInTag = node?.data?.propType === NODE_TYPES.TAG;

  if (isLinkInTag) {
    const prefixLinkWithoutTag = removeLastSectionOfPathname(prefixLink);
    return isDestinationNestedInLinkFrom(prefixLinkWithoutTag, 2);
  }

  return false;
};

const getMdHeadingLinkPrefix = (
  originalPrefix,
  shouldLinkToGeneratedDescriptionGuide,
  _isLinkToCurrentPrefix,
  mdNameOfDestination,
) => {
  if (!shouldLinkToGeneratedDescriptionGuide) {
    return mdNameOfDestination && _isLinkToCurrentPrefix
      ? sectionPrefix(originalPrefix, mdNameOfDestination)
      : originalPrefix;
  }
  const guideUrlFormat = toUrlFormat(NEW_GUIDE_NAME);
  return sectionPrefix(originalPrefix, guideUrlFormat);
};

const isInDocsTree = (relativeUrl, store) => {
  const memberId = getMemberId(relativeUrl, store);
  return isLinkExistInDocs(store.getFullTree(), `/${memberId}`);
};

const buildLinkToMdHeading = (prefix, relativeUrl) => {
  const relativeUrlLowerCased = relativeUrl.toLowerCase();
  const headingText = toUrlFormat(
    replaceMdLinksSpaces(getPathnameSuffix(relativeUrlLowerCased)),
  );
  return getMdHeadingId(prefix, headingText);
};

const fromUrlToMdHeadingIfNeeded = (wixDocsUrlToArticle, store, prefix) => {
  const shouldLinkToMdHeading =
    wixDocsUrlToArticle !== '/' && !isInDocsTree(wixDocsUrlToArticle, store);
  if (shouldLinkToMdHeading) {
    const shouldLinkToGeneratedDescriptionGuide =
      _shouldLinkToGeneratedDescriptionGuide(wixDocsUrlToArticle, store);

    const _isLinkToCurrentPrefix = isLinkToCurrentPrefix(
      wixDocsUrlToArticle,
      prefix,
      store,
      shouldLinkToGeneratedDescriptionGuide,
    );

    const linkPrefix = _isLinkToCurrentPrefix
      ? prefix
      : removeStartingSlash(removeLastSectionOfPathname(wixDocsUrlToArticle));

    const mdNameOfDestination = removeStartingSlash(
      wixDocsUrlToArticle.replace(`/${linkPrefix}`, ''),
    ).split('/')[0];

    const mdHeadingLinkPrefix = getMdHeadingLinkPrefix(
      linkPrefix,
      shouldLinkToGeneratedDescriptionGuide,
      _isLinkToCurrentPrefix,
      mdNameOfDestination,
    );

    const mdHeadingId = buildLinkToMdHeading(
      mdHeadingLinkPrefix,
      wixDocsUrlToArticle,
    );
    return `/${mdHeadingLinkPrefix}#${mdHeadingId}`;
  }
  return wixDocsUrlToArticle;
};

const toWixDocsUrl = (url, pathname, store, prefix, mdUrlFormat) => {
  const [beforeHashOldUrl, hashPartOldUrl] = url.split('#');
  const beforeHashWithoutHtmlOldUrl = beforeHashOldUrl
    .split('.')
    .filter((_) => _ !== 'html' && _ !== '');
  const categoryFromPathname = getCategoryNameFromPathname(pathname, store);
  const prefixParts = prefix.split('/');
  const isLinkToDifferentSection =
    beforeHashWithoutHtmlOldUrl.length > 0 &&
    beforeHashWithoutHtmlOldUrl.map((str) => str.toLowerCase()) !== prefixParts;
  const pathnameWithoutRouterBasename = removeRouterBasename(
    pathname,
    store.storePath,
  );
  const isLinkToFirstDepthNode =
    categoryFromPathname === prefix &&
    pathnameWithoutRouterBasename === `/${prefix}`;

  const beforeHashWithoutCategoryOldUrl = beforeHashWithoutHtmlOldUrl.splice(1);
  const fullHash = [
    ...beforeHashWithoutCategoryOldUrl,
    ...((hashPartOldUrl && hashPartOldUrl.split('.')) || []),
  ];

  const shouldAddPrefix =
    hashPartOldUrl &&
    hashPartOldUrl.length > 0 &&
    !isLinkToDifferentSection &&
    !isLinkToFirstDepthNode;

  const categoryFromOldUrl =
    beforeHashWithoutHtmlOldUrl.length && beforeHashWithoutHtmlOldUrl[0];
  const shouldAddStartingSlash =
    !shouldAddPrefix &&
    isRelativeUrl(url) &&
    ((!url.startsWith('#') && !url.startsWith('/')) ||
      (!categoryFromOldUrl && categoryFromPathname));

  const categoryToPresentInUrl = categoryFromOldUrl
    ? categoryFromOldUrl
    : categoryFromPathname;

  const hashParts = getHashPart(
    fullHash,
    prefix,
    shouldAddPrefix,
    mdUrlFormat,
    pathnameWithoutRouterBasename,
    store,
  );
  return `${shouldAddStartingSlash ? '/' : ''}${
    !shouldAddPrefix ? categoryToPresentInUrl : ''
  }${hashParts}`;
};

export const replaceAbsoluteLinkToRelativeIfNeeded = (store, url) => {
  const host = store.host;
  const basename = store.storePath;
  const hostAndBaseUrl = `${host}${basename}`;
  const isUrlToSameSite =
    removeStartingProtocol(url).startsWith(hostAndBaseUrl);
  if (isUrlToSameSite) {
    const pathname = removeStartingProtocol(url).replace(hostAndBaseUrl, '');
    if (store.isPageExistInDocs(pathname)) {
      return pathname;
    }
  }
  return url;
};

export const transformToWixDocsUrlMapping =
  (store, pathname = '', prefix = '', mdUrlFormat = '') =>
  (url = '') => {
    try {
      if (isVeloSite(store.viewer.category) && isAbsoluteOldCorvidUrl(url)) {
        if (
          removeStartingProtocol(url).startsWith(`${corvidApiBaseUrl}/spis/`) || // TODO remove this line once migration to velo is over
          removeStartingProtocol(url).startsWith(`${veloApiBaseUrl}/spis/`)
        ) {
          return url;
        }
        return transformToWixDocsUrlMapping(
          store,
          pathname,
          prefix,
          mdUrlFormat,
        )(removeStartingProtocol(removeCorvidOldApiBaseUrl(url)));
      }

      if (!isRelativeUrl(url) || isMailUrl(url)) {
        return replaceAbsoluteLinkToRelativeIfNeeded(store, url) || url;
      }

      if (isRelativeLinkToMdFile(url)) {
        const relativeMdLink = getRelativeMdLink(url, store);
        if (relativeMdLink) {
          return relativeMdLink;
        }
      }
      const wixDocsUrlToArticle = toWixDocsUrl(
        url,
        pathname,
        store,
        prefix,
        mdUrlFormat,
      );
      return fromUrlToMdHeadingIfNeeded(
        wixDocsUrlToArticle.toLowerCase(),
        store,
        prefix,
      );
    } catch (e) {
      console.error(
        `Failed to transform the url: ${url} to wix docs url, error: ${e}`,
      );
    }
  };
